import { ItemDefinition } from 'types';
import { useIsMoveUPO } from 'hooks/useIsMoveUPO';
import orderBy from 'lodash/orderBy';
import pickBy from 'lodash/pickBy';
import values from 'lodash/values';
import partition from 'lodash/partition';
import { Item } from 'types/item';
import { ItemKinds } from 'types/item-kinds';
import { useSWRWhenAuthed } from 'utils/use-swr-when-authed';
import { useGetUserInfoQuery } from './use-get-user-info-query';
import { useEssentialsTabCards } from './use-tab-cards';
import { CardData } from '../types/cards';
import { useMemo } from 'react';
import { useFeature } from '@growthbook/growthbook-react';
import {
  DivisionUtilitiesConfig,
  useGetUtilitiesConfigQuery,
} from '@updater/consumer-graph';

const importanceScoringConfig = require('flows/core/constants/daisy-chain-importance-scoring.json');

const CUSTOM_TASK_PREFIX = 'resident_onboarding-custom_task';

const UTILITY_KIND_TO_CONFIG_KEY: Partial<
  Record<string, keyof DivisionUtilitiesConfig>
> = {
  [ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_ELECTRIC]: 'electricRequired',
  [ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_WATER]: 'waterRequired',
  [ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_GAS]: 'gasRequired',
};

const isUtilityRequiredByConfig = (
  code: string,
  utilitiesConfig: DivisionUtilitiesConfig | undefined
): boolean => {
  const configKey = UTILITY_KIND_TO_CONFIG_KEY[code];
  return configKey ? Boolean(utilitiesConfig?.[configKey]) : false;
};

type ImportanceScoringDictionary = {
  [key: string]: {
    [key: string]: number;
  };
};

const importanceScoringDictionary: ImportanceScoringDictionary = {};

Object.keys(importanceScoringConfig).map((task) => {
  importanceScoringDictionary[task] = {};
  Object.keys(importanceScoringConfig[task]).map((scoreKey) => {
    importanceScoringDictionary[task][scoreKey] = Number(
      importanceScoringConfig[task][scoreKey].split(',').join('')
    );
  });
});

// NOTE: we'll use this until we properly type items
export type KindType = keyof typeof itemKindMap;

export enum ItemGroup {
  UTILITIES = 'utilities',
}

export type DaisyChainItemKind = {
  kind: KindType;
  route: string;
  id: string;
  importanceScore: number;
  status: string;
  itemGroup?: ItemGroup;
};

/**
 * Uses importance score from card makers.
 */
export const mapKindToImportanceScore = ({
  kind,
  status,
}: {
  kind: KindType;
  status: string;
}): number => {
  if (kind in itemKindMap) {
    const cardStepName = itemKindMap[kind]?.scoreKey || '';
    if (cardStepName in importanceScoringDictionary) {
      const cardItem = importanceScoringDictionary[cardStepName];

      if (status in cardItem) {
        return cardItem[status as keyof typeof cardItem];
      }

      // Fall back to 'default' score for unrecognised statuses so that a task
      // with an unexpected status (e.g. 'info_requested', null) is still ranked
      // by its baseline priority rather than dropping to 0.
      if ('default' in cardItem) {
        return cardItem['default' as keyof typeof cardItem];
      }
    }
  }

  return 0;
};

export const findEaseScore = ({ kind }: { kind: KindType }): number => {
  if (kind in itemKindMap) {
    const cardStepName = itemKindMap[kind]?.scoreKey || '';
    if (cardStepName in importanceScoringDictionary) {
      const cardItem = importanceScoringDictionary[cardStepName];

      if ('easePriority' in cardItem) {
        return cardItem['easePriority' as keyof typeof cardItem];
      }
    }
  }

  return 0;
};

export const getRouteForPOI = (
  insuranceItemDef: ItemDefinition | undefined
): string => {
  const assurantEnabled = !!insuranceItemDef?.page?.assurant;
  // defaulting to /deals if no route
  return assurantEnabled
    ? `/insurance/provider`
    : itemKindMap['resident_onboarding-proof-of-insurance']?.route || '/deals';
};

export const getAllRequiredItemsForUPO = ({
  itemsData,
  itemDefinitionsData,
  toDoCards,
  isGreystarPilotEnabled,
  utilitiesConfig,
}: {
  itemsData: Item[];
  itemDefinitionsData: ItemDefinition[];
  toDoCards: CardData[];
  isGreystarPilotEnabled: boolean;
  utilitiesConfig?: DivisionUtilitiesConfig;
}): DaisyChainItemKind[] => {
  // if 'resident_onboarding-proof-of-insurance' exists, exclude 'insurance' itemDef
  const proofOfInsuranceDefsExist = Boolean(
    itemDefinitionsData?.find(
      ({ code }: { code: string }) =>
        code === ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_INSURANCE
    )
  );
  const excludedItemCodes: string[] = [];
  const insuranceItemDef = itemDefinitionsData?.find(
    ({ code }: { code: string }) => code === ItemKinds.INSURANCE
  );
  if (proofOfInsuranceDefsExist) {
    excludedItemCodes.push(ItemKinds.INSURANCE);
  }
  // If the greystar assurant static pilot is enabled for this property we don't want them going through UPO POI flow
  // and just want them going to /insurance/assurant
  if (isGreystarPilotEnabled) {
    excludedItemCodes.push(ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_INSURANCE);
  }
  // Get the user's required tasks AND TVI.
  // Utility tasks use utilitiesConfig instead of page.required.
  const requiredTasks = itemDefinitionsData?.filter(
    ({ page, code }) =>
      (page?.required ||
        code === 'tv_internet' ||
        isUtilityRequiredByConfig(code, utilitiesConfig)) &&
      !excludedItemCodes.includes(code)
  );

  // Get the status from the API and map code to kind
  const requiredTasksWithStatus =
    requiredTasks
      .map(({ code }: { code: string }) => {
        const itemStatusObject = itemsData.find(
          // @ts-expect-error FIXME: TS7030 - strict mode migration
          ({ kind }: { kind: string }) => {
            if (kind !== undefined) {
              return kind === code;
            }
          }
        );

        /* All other UPO tasks are pre-created, TVI and insurance are not */
        // NOTE: `tv_internet` and `insurance` are not in item tracking by default.
        if (itemStatusObject === undefined && code === 'tv_internet') {
          return {
            kind: 'tv_internet',
            id: 'tv_internet',
            status: 'started', // if not "started" won't sort as first, most important item
          };
        }
        if (itemStatusObject === undefined && code === 'insurance') {
          if (
            toDoCards?.filter(
              ({ data }) => data.subtype === 'SYI' && data.required
            ).length
          ) {
            return {
              kind: 'insurance',
              id: 'insurance',
              status: 'started', // if not "started" won't sort as first, most important item
            };
          }
        }

        return itemStatusObject;
      })
      .filter(Boolean) || [];

  // Omit all fields not needed for view.
  const scrubbedItemsForDaisyChain = requiredTasksWithStatus
    // @ts-expect-error FIXME: TS7030 - strict mode migration
    .map((item) => {
      if (item && 'kind' in item && 'id' in item && 'status' in item) {
        const kind = item.kind as KindType;
        const id = item.id as string;
        const status = item.status as string;

        // Custom tasks don't need to be in itemKindMap
        const isCustomTask = kind.startsWith(CUSTOM_TASK_PREFIX);

        // Ensure key exists in itemKindMap (unless it's a custom task)
        if (!isCustomTask && !(kind in itemKindMap)) {
          return;
        }
        // note: we need to do custom routing when Assurant is enabled
        // let's come up with a cleaner way to do this
        // defaulting to /deals if no route
        const route: string = isCustomTask
          ? `/resident-onboarding/${kind.replace('resident_onboarding-', '')}`
          : kind === 'resident_onboarding-proof-of-insurance'
          ? getRouteForPOI(insuranceItemDef)
          : itemKindMap[kind]?.route || '/deals';

        const itemGroup = itemKindMap[kind]?.itemGroup;
        const importanceScore = isCustomTask
          ? 0 // Custom tasks have no importance score, will be appended at the end
          : mapKindToImportanceScore({
              kind,
              status,
            });
        const easePriority = isCustomTask ? 0 : findEaseScore({ kind });

        return {
          kind,
          itemGroup,
          route,
          id,
          importanceScore,
          status,
          easePriority,
        };
      }
    })
    ?.filter(Boolean) as DaisyChainItemKind[];

  return scrubbedItemsForDaisyChain;
};

export const useDaisyChain = () => {
  const { data: itemDefinitionsData } = useSWRWhenAuthed(
    '/v2/item_definitions'
  );
  const { data: rawItemsData } = useSWRWhenAuthed('/v2/items');
  const { data: userData } = useGetUserInfoQuery();
  const currentMoveId = userData?.getUser?.user?.currentMove?.id;
  const itemsData = rawItemsData?.filter(({ move_id }: { move_id: number }) =>
    currentMoveId ? move_id?.toString() === currentMoveId : true
  );
  const { toDoCards } = useEssentialsTabCards();
  // need to make sure that if part of greystar assurant STATIC landing that we override routing to /insurance/assurant
  const { value: greystarAssurantPilotValue } = useFeature(
    'greystar_assurant_pilot_properties'
  );
  const isGreystarPilotEnabled = greystarAssurantPilotValue?.active === true;

  const entityId = itemDefinitionsData?.find(
    (itemDef: ItemDefinition) => itemDef?.entity?.id
  )?.entity?.id;
  const { data: utilitiesConfigData } = useGetUtilitiesConfigQuery({
    variables: { entityId },
    skip: !entityId,
  });
  const utilitiesConfig = utilitiesConfigData?.utilitiesConfig ?? undefined;

  // Wait for utilitiesConfig when an entityId is available
  const utilitiesReady = !entityId || !!utilitiesConfig;
  const loading = !(
    itemsData &&
    userData &&
    toDoCards &&
    itemDefinitionsData &&
    utilitiesReady
  );

  if (loading)
    return {
      completedItems: undefined,
      allItems: undefined,
      nextItem: undefined,
      unfinishedItems: undefined,
      loading: true,
    };

  const allItems = getAllRequiredItemsForUPO({
    itemsData,
    itemDefinitionsData,
    toDoCards,
    isGreystarPilotEnabled,
    utilitiesConfig,
  });
  /**
   * Items with status === 'completed' | 'validating' | 'pending'.
   */

  const [completedItems, unsortedUnfinishedItems] = partition(
    allItems,
    ({ status }) =>
      status === 'completed' || status === 'validating' || status === 'pending'
  );

  // Separate custom tasks from regular tasks (only within unfinished items)
  const [customTasks, regularTasks] = partition(
    unsortedUnfinishedItems,
    ({ kind }) => kind.startsWith(CUSTOM_TASK_PREFIX)
  );

  // Sort regular tasks by importance score
  const sortedRegularTasks = orderBy(regularTasks, 'importanceScore', 'desc');

  // Append custom tasks at the end in API order
  const unfinishedItems = [...sortedRegularTasks, ...customTasks];
  /**
   * Returns highest score which is the first item in unfinishedItems.
   */
  const nextItem = unfinishedItems[0] ?? null;
  console.log({ unfinishedItems, allItems, nextItem });
  return {
    completedItems,
    allItems,
    nextItem,
    unfinishedItems,
    loading: false,
  };
};

/**
 * Returns ascending sorted tasks based on the `easePriority` key.
 * NOTE: items that are missing the `easePriority` key will be omitted from this list.
 */
export const useEasyTasks = () => {
  const { unfinishedItems, loading } = useDaisyChain();

  const easyTasksData = useMemo(() => {
    if (loading)
      return {
        easyTasks: [],
        loading: true,
      };

    const tasksWithEasePriorityKey = values(
      pickBy(unfinishedItems, (unfinishedItems) =>
        unfinishedItems.hasOwnProperty('easePriority')
      )
    );
    const easyTasks = orderBy(tasksWithEasePriorityKey, 'easePriority', 'asc');

    return {
      easyTasks,
      loading: false,
    };
  }, [loading, unfinishedItems]);
  return easyTasksData;
};

export const useShouldRouteToUPODaisyChain = () => {
  const { data: { isUPO } = {} } = useIsMoveUPO();
  const { on: daisyChainEnabled } = useFeature('consumer_upo_daisy_chain');
  return isUPO && daisyChainEnabled;
};

export type ItemKindMapType = {
  [s in ItemKinds]?: {
    scoreKey: keyof typeof importanceScoringDictionary;
    route: string;
    itemGroup?: ItemGroup;
  };
};

/**
 * Map item kind to { route + scoreKey }
 */
export const itemKindMap: ItemKindMapType = {
  [ItemKinds.FORWARD_MAIL]: { scoreKey: 'FYM', route: '/forward-mail' },
  [ItemKinds.MOVE_YOUR_STUFF]: { scoreKey: 'MYS', route: '/TODO' },
  //[ItemKinds.TV_INTERNET]: { scoreKey: 'TVI', route: '/tvi/landing' },
  [ItemKinds.TV_INTERNET]: { scoreKey: 'TVI', route: '/tvi' },
  [ItemKinds.RESIDENT_ONBOARDING_PET_REGISTRATION]: {
    scoreKey: 'PET',
    route: '/resident-onboarding/pets',
  },
  [ItemKinds.RESIDENT_ONBOARDING_MOVE_IN_COSTS]: {
    scoreKey: 'MOVE_IN_COSTS',
    route: '/resident-onboarding/move-costs',
  },
  [ItemKinds.RESIDENT_ONBOARDING_VEHICLE_REGISTRATION]: {
    scoreKey: 'CAR',
    route: '/resident-onboarding/vehicles/has-vehicle',
  },
  [ItemKinds.RESIDENT_ONBOARDING_DOCUMENT_REVIEW]: {
    scoreKey: 'REVIEW_DOCUMENT',
    route: '/resident-onboarding/document-review',
  },
  [ItemKinds.RESIDENT_ONBOARDING_SECURITY_DEPOSIT_ALTERNATIVE]: {
    scoreKey: 'SECURITY_DEPOSIT_ALTERNATIVE',
    route: '/resident-onboarding/security-deposit-alternative/intro',
  },
  [ItemKinds.RESIDENT_ONBOARDING_DOWNLOAD_COMMUNITY_APP]: {
    scoreKey: 'COMMUNITY_APP',
    route: '/resident-onboarding/download-community-app',
  },
  [ItemKinds.RESIDENT_ONBOARDING_LOADING_DOCK_RESERVATION]: {
    scoreKey: 'LOADING_DOCK',
    route: '/resident-onboarding/loading-dock-reservation',
  },
  [ItemKinds.RESIDENT_ONBOARDING_ELEVATOR_SCHEDULING]: {
    scoreKey: 'ELEVATOR',
    route: '/resident-onboarding/elevator-scheduling/register',
  },
  [ItemKinds.RESIDENT_ONBOARDING_KEY_PICKUP]: {
    scoreKey: 'KEY',
    route: '/resident-onboarding/key-pickup/register',
  },
  [ItemKinds.RESIDENT_ONBOARDING_PACKAGES_AND_DELIVERIES]: {
    scoreKey: 'PACKAGES',
    route: '/resident-onboarding/packages-and-deliveries/register',
  },
  // NOTE: There's different types of landing pages
  [ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_INSURANCE]: {
    scoreKey: 'INSURANCE_RESIDENT_ONBOARDING',
    route: '/insurance/landing',
  },
  [ItemKinds.INSURANCE]: {
    scoreKey: 'INSURANCE',
    route: '/insurance/provider',
  },
  [ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_WATER]: {
    scoreKey: 'ROB_UTILITY',
    route: '/resident-onboarding/proof-of-utilities/set-up/water',
    itemGroup: ItemGroup.UTILITIES,
  },
  [ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_ELECTRIC]: {
    scoreKey: 'ROB_UTILITY',
    route: '/resident-onboarding/proof-of-utilities/set-up/electric',
    itemGroup: ItemGroup.UTILITIES,
  },
  [ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_GAS]: {
    scoreKey: 'ROB_UTILITY',
    route: '/resident-onboarding/proof-of-utilities/set-up/gas',
    itemGroup: ItemGroup.UTILITIES,
  },
  [ItemKinds.RESIDENT_ONBOARDING_REVIEW_COMMUNITY_FEES]: {
    scoreKey: 'REVIEW_COMMUNITY_FEES',
    route: '/resident-onboarding/review-community-fees',
  },
  [ItemKinds.RESIDENT_ONBOARDING_REVIEW_RESIDENT_HANDBOOK]: {
    scoreKey: 'REVIEW_RESIDENT_HANDBOOK',
    route: '/resident-onboarding/review-resident-handbook',
  },
  [ItemKinds.RESIDENT_ONBOARDING_PET_POLICY_DOCUMENTATION]: {
    scoreKey: 'PET_POLICY_DOCUMENTATION',
    route: '/resident-onboarding/pet-policy-documentation',
  },
  [ItemKinds.RESIDENT_ONBOARDING_ACCESS_RESIDENT_PORTAL]: {
    scoreKey: 'ACCESS_RESIDENT_PORTAL',
    route: '/resident-onboarding/access-resident-portal',
  },
};
